import {ClientTypeEnum} from '@/app/features/clients/models/client.model';

export const ALL_CLIENT_TYPES: ClientTypeEnum[] = [
  ClientTypeEnum.INDIVIDUAL,
  ClientTypeEnum.FACILITY,
  ClientTypeEnum.GOVERNMENTAL,
];

/** Matches BE `TenantGeneralSettings` */
export interface TenantGeneralSettings {
  allowedClientTypes?: ClientTypeEnum[];
  wathqVerificationEnabled?: boolean;
}

export interface TenantRegistrationConfig {
  allowedClientTypes: ClientTypeEnum[];
  allowWathq: boolean;
}

export const DEFAULT_REGISTRATION_CONFIG: TenantRegistrationConfig = {
  allowedClientTypes: ALL_CLIENT_TYPES,
  allowWathq: true,
};

export type FacilityVerificationMethod = 'wathq' | 'admin';

export function resolveAllowedClientTypes(
  types?: ClientTypeEnum[] | null,
): ClientTypeEnum[] {
  if (!types?.length) return [...ALL_CLIENT_TYPES];
  const allowed = types.filter((t) => ALL_CLIENT_TYPES.includes(t));
  return allowed.length ? allowed : [...ALL_CLIENT_TYPES];
}

export function isSingleAllowedType(types: ClientTypeEnum[]): boolean {
  return types.length === 1;
}

export function parseAllowedClientTypes(
  value: unknown,
): ClientTypeEnum[] | undefined {
  if (Array.isArray(value)) {
    return resolveAllowedClientTypes(value as ClientTypeEnum[]);
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (Array.isArray(parsed)) {
        return resolveAllowedClientTypes(parsed as ClientTypeEnum[]);
      }
    } catch {
      return undefined;
    }
  }
  return undefined;
}

export function parseGeneralSettings(
  value: unknown,
): TenantGeneralSettings | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') {
    try {
      return parseGeneralSettings(JSON.parse(value));
    } catch {
      return undefined;
    }
  }
  if (typeof value === 'object') {
    const raw = value as Record<string, unknown>;
    const allowedClientTypes = parseAllowedClientTypes(
      raw['allowedClientTypes'],
    );
    let wathqVerificationEnabled: boolean | undefined;
    const wathq = raw['wathqVerificationEnabled'];
    if (typeof wathq === 'boolean') {
      wathqVerificationEnabled = wathq;
    } else if (wathq === 'true') {
      wathqVerificationEnabled = true;
    } else if (wathq === 'false') {
      wathqVerificationEnabled = false;
    }
    return {
      ...(allowedClientTypes ? {allowedClientTypes} : {}),
      ...(wathqVerificationEnabled !== undefined
        ? {wathqVerificationEnabled}
        : {}),
    };
  }
  return undefined;
}

export function toRegistrationConfig(
  settings?:
    | {
        generalSettings?: TenantGeneralSettings | null;
        /** @deprecated legacy flat shape */
        allowedClientTypes?: ClientTypeEnum[] | null;
        /** @deprecated legacy flat shape */
        allowWathq?: boolean;
      }
    | TenantGeneralSettings
    | null,
): TenantRegistrationConfig {
  const general =
    settings && 'generalSettings' in settings
      ? (parseGeneralSettings(settings.generalSettings) ??
        ({
          allowedClientTypes: settings.allowedClientTypes,
          wathqVerificationEnabled: settings.allowWathq,
        } as TenantGeneralSettings))
      : parseGeneralSettings(settings);

  return {
    allowedClientTypes: resolveAllowedClientTypes(general?.allowedClientTypes),
    allowWathq: general?.wathqVerificationEnabled ?? true,
  };
}
